const SHIPPING_INSURANCE_ATTR = '_shipping_insurance_added';

let insuranceQueue = Promise.resolve();

function enqueueInsuranceTask(task) {
  const run = insuranceQueue.then(task, task);
  insuranceQueue = run;
  return run;
}

function isInsuranceLine(item, handle) {
  if (!item) return false;
  if (handle && item.handle === handle) return true;
  return !!(item.properties && item.properties._shipping_insurance === 'true');
}

class ShippingInsurance extends HTMLElement {
  constructor() {
    super();
    
    // Bind methods to ensure 'this' context is correct when used as event handlers
    this.handleToggleChange = this.handleToggleChange.bind(this);
    this.handleImageError = this.handleImageError.bind(this);
    this.handleImageLoad = this.handleImageLoad.bind(this);
  }

  get insuranceRoot() {
    return this.querySelector('.shipping-insurance') || this;
  }

  get productHandle() {
    return this.insuranceRoot.dataset.productHandle;
  }

  get variantId() {
    return this.toggle?.dataset.variantId || this.insuranceRoot.dataset.variantId;
  }

  connectedCallback() {
    this.toggle = this.querySelector('.shipping-insurance__toggle');
    this.container = this.querySelector('.shipping-insurance__container');
    this.loadingElement = this.querySelector('.shipping-insurance__loading');
    this.errorElement = this.querySelector('.shipping-insurance__error');
    this.errorText = this.querySelector('.shipping-insurance__error-text');
    this.productImage = this.querySelector('.shipping-insurance__image');
    
    if (this.toggle) {
      this.toggle.addEventListener('change', this.handleToggleChange);
    }
    
    // Handle image loading errors
    if (this.productImage) {
      this.productImage.addEventListener('error', this.handleImageError);
      this.productImage.addEventListener('load', this.handleImageLoad);
    }

    // Drawer redraws replace this element. Only add insurance once, and never
    // after the customer has turned it off or deleted the line.
    enqueueInsuranceTask(() => this.maybeAutoAdd());
  }
  
  disconnectedCallback() {
    if (this.toggle) {
      this.toggle.removeEventListener('change', this.handleToggleChange);
    }
    if (this.productImage) {
      this.productImage.removeEventListener('error', this.handleImageError);
      this.productImage.removeEventListener('load', this.handleImageLoad);
    }
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
  
  handleToggleChange(event) {
    event.preventDefault();
    
    const isChecked = event.target.checked;
    const variantId = event.target.dataset.variantId;

    if (isChecked) {
      window.__shippingInsuranceOptOut = false;
      this.addInsuranceToCart(variantId);
    } else {
      window.__shippingInsuranceOptOut = true;
      this.removeInsuranceFromCart(variantId);
    }
  }
  
  handleImageError(event) {
    console.warn('Shipping insurance product image failed to load');
    // Hide the image and show fallback icon
    const image = event.target;
    const icon = this.querySelector('.shipping-insurance__icon');
    const svgWrapper = icon.querySelector('.svg-wrapper');
    
    if (image && svgWrapper) {
      image.style.display = 'none';
      svgWrapper.style.display = 'flex';
      svgWrapper.style.alignItems = 'center';
      svgWrapper.style.justifyContent = 'center';
    }
  }
  
  handleImageLoad(event) {
    // Image loaded successfully, hide the fallback icon
    const icon = this.querySelector('.shipping-insurance__icon');
    const svgWrapper = icon.querySelector('.svg-wrapper');
    
    if (svgWrapper) {
      svgWrapper.style.display = 'none';
    }
  }
  
  rememberInsuranceAdded() {
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { [SHIPPING_INSURANCE_ATTR]: 'true' }
      })
    })
      .then(() => {
        if (!window.__shippingInsuranceOptOut) return null;
        return fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: { [SHIPPING_INSURANCE_ATTR]: 'false' }
          })
        });
      })
      .catch((error) => {
        console.error('Error saving shipping insurance preference:', error);
      });
  }

  async fetchCart() {
    const response = await fetch(window.Shopify.routes.root + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch current cart information');
    }
    return response.json();
  }

  async maybeAutoAdd() {
    if (!this.isConnected) return;

    if (window.__shippingInsuranceCartWasEmpty === true) {
      window.__shippingInsuranceOptOut = false;
    }

    // First product in an empty cart: add insurance immediately, without a
    // separate cart read. The drawer has just rendered and insurance is not in it.
    if (
      window.__shippingInsuranceCartWasEmpty === true &&
      this.variantId &&
      !window.__shippingInsuranceOptOut
    ) {
      window.__shippingInsuranceCartWasEmpty = false;
      if (this.toggle?.checked) {
        this.rememberInsuranceAdded();
        return;
      }
      if (this.toggle) this.toggle.checked = true;
      await this.addInsuranceToCart(this.variantId, { fromQueue: true, skipCartCheck: true });
      return;
    }

    if (window.__shippingInsuranceOptOut) {
      if (this.toggle) this.toggle.checked = false;
      return;
    }

    let cart;
    try {
      cart = await this.fetchCart();
    } catch (error) {
      console.error('Error reading cart for shipping insurance:', error);
      return;
    }

    if (!this.isConnected || !cart) return;
    if (window.__shippingInsuranceOptOut) {
      if (this.toggle) this.toggle.checked = false;
      return;
    }

    const handle = this.productHandle;
    const variantId = this.variantId;
    const preference = cart.attributes && cart.attributes[SHIPPING_INSURANCE_ATTR];
    const items = cart.items || [];
    const insuranceItem = items.find((item) => isInsuranceLine(item, handle));
    const hasRealItems = items.some((item) => !isInsuranceLine(item, handle));

    if (insuranceItem && !hasRealItems) {
      if (this.toggle) this.toggle.checked = false;
      await this.removeInsuranceFromCart(insuranceItem.variant_id, { fromQueue: true, system: true });
      return;
    }

    if (insuranceItem) {
      if (this.toggle) this.toggle.checked = true;
      return;
    }

    if (this.toggle) this.toggle.checked = false;

    const freshCart = window.__shippingInsuranceCartWasEmpty === true;

    if (!freshCart && preference === 'false') {
      window.__shippingInsuranceOptOut = true;
      return;
    }

    // Attribute "true" means we already added it for this cart. If the line is
    // gone, the customer removed it — do not put it back when the drawer redraws.
    // A fresh cart (empty, then the first product) always gets insurance.
    if (!hasRealItems || !variantId) return;
    if (!freshCart && preference === 'true') return;

    window.__shippingInsuranceCartWasEmpty = false;
    if (this.toggle) this.toggle.checked = true;
    await this.addInsuranceToCart(variantId, { fromQueue: true, cart });
  }

  async addInsuranceToCart(variantId, options = {}) {
    const run = async () => {
      if (window.__shippingInsuranceOptOut) {
        if (this.isConnected && this.toggle) this.toggle.checked = false;
        return;
      }

      this.showLoading();
      this.hideError();

      try {
        if (!variantId || variantId === 'null' || variantId === 'undefined') {
          throw new Error('Invalid product variant. Please refresh the page and try again.');
        }

        if (!options.skipCartCheck) {
          const cart = options.cart || await this.fetchCart();
          const alreadyInCart = (cart.items || []).some(
            (item) => isInsuranceLine(item, this.productHandle) || String(item.variant_id) === String(variantId)
          );

          if (alreadyInCart) {
            if (this.isConnected && this.toggle) this.toggle.checked = true;
            return;
          }
        }

        if (window.__shippingInsuranceOptOut) {
          if (this.isConnected && this.toggle) this.toggle.checked = false;
          return;
        }

        const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates: { [variantId]: 1 },
            attributes: { [SHIPPING_INSURANCE_ATTR]: 'true' },
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          let errorMessage = 'Failed to add shipping insurance';
          if (errorData.description) {
            errorMessage = errorData.description;
          } else if (response.status === 422) {
            errorMessage = 'This product is currently unavailable';
          } else if (response.status >= 500) {
            errorMessage = 'Server error. Please try again later.';
          }
          throw new Error(errorMessage);
        }

        const updatedCartData = await response.json();

        if (window.__shippingInsuranceOptOut) {
          await this.removeInsuranceFromCart(variantId, { fromQueue: true });
          return;
        }

        if (updatedCartData.sections) {
          await this.updateCartDrawer(updatedCartData.sections);
        } else {
          await this.updateCartDrawer();
        }

        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'shipping-insurance',
            cartData: updatedCartData,
            action: 'add'
          });
        }
      } catch (error) {
        console.error('Error adding shipping insurance:', error);
        this.showError(error.message);
        if (this.isConnected && this.toggle) this.toggle.checked = false;
      } finally {
        this.hideLoading();
      }
    };

    if (options.fromQueue) return run();
    return enqueueInsuranceTask(run);
  }
  
  async removeInsuranceFromCart(variantId, options = {}) {
    const run = async () => {
      if (!options.system) window.__shippingInsuranceOptOut = true;

      this.showLoading();
      this.hideError();

      try {
        if (!variantId) variantId = this.variantId;

        if (!variantId || variantId === 'null' || variantId === 'undefined') {
          throw new Error('Invalid product variant for removal.');
        }

        const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates: { [variantId]: 0 },
            attributes: {
              [SHIPPING_INSURANCE_ATTR]: options.system ? '' : 'false'
            }
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          let errorMessage = 'Failed to remove shipping insurance';
          if (errorData.description) {
            errorMessage = errorData.description;
          } else if (response.status >= 500) {
            errorMessage = 'Server error. Please try again later.';
          }
          throw new Error(errorMessage);
        }

        const updatedCartData = await response.json();
        await this.updateCartDrawer();

        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'shipping-insurance',
            cartData: updatedCartData,
            action: 'remove'
          });
        }
      } catch (error) {
        console.error('Error removing shipping insurance:', error);
        if (!options.system) window.__shippingInsuranceOptOut = false;
        this.showError(error.message);
        if (this.isConnected && this.toggle) this.toggle.checked = true;
      } finally {
        this.hideLoading();
      }
    };

    if (options.fromQueue) return run();
    if (!options.system) window.__shippingInsuranceOptOut = true;
    return enqueueInsuranceTask(run);
  }
  
  async updateCartDrawer(sections = null) {
    try {
      if (sections) {
        // If sections are returned from cart/change.js, use them
        const cartDrawerSection = sections['cart-drawer'];
        if (cartDrawerSection) {
          const cartDrawerHtml = new DOMParser().parseFromString(cartDrawerSection, 'text/html');

          const newCartDrawerItems = cartDrawerHtml.querySelector('cart-drawer-items');
          const currentCartDrawerItems = document.querySelector('cart-drawer-items');
          if (newCartDrawerItems && currentCartDrawerItems) {
            currentCartDrawerItems.replaceWith(newCartDrawerItems);
          }

          const newCartFooter = cartDrawerHtml.querySelector('.cart-drawer__footer');
          const currentCartFooter = document.querySelector('.cart-drawer__footer');
          if (newCartFooter && currentCartFooter) {
            currentCartFooter.replaceWith(newCartFooter);
          }
        }
        
        const cartIconBubbleSection = sections['cart-icon-bubble'];
        if (cartIconBubbleSection) {
          const newCartIconBubble = new DOMParser().parseFromString(cartIconBubbleSection, 'text/html').querySelector('.shopify-section');
          const currentCartIconBubble = document.getElementById('cart-icon-bubble');
          if (newCartIconBubble && currentCartIconBubble) {
            currentCartIconBubble.innerHTML = newCartIconBubble.innerHTML;
          }
        }
        return;
      }

      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      
      
      // Update cart drawer items
      const newCartDrawerItems = html.querySelector('cart-drawer-items');
      const currentCartDrawerItems = document.querySelector('cart-drawer-items');

      // console.log("newCartDrawerItems",newCartDrawerItems);
     
      
      if (newCartDrawerItems && currentCartDrawerItems) {
        currentCartDrawerItems.replaceWith(newCartDrawerItems);
      }
      
      // Update cart drawer footer
      const newCartFooter = html.querySelector('.cart-drawer__footer');
      const currentCartFooter = document.querySelector('.cart-drawer__footer');
      
      if (newCartFooter && currentCartFooter) {
        currentCartFooter.replaceWith(newCartFooter);
      }
      
      // Update cart icon bubble
      const cartIconBubbleResponse = await fetch(`${window.location.pathname}?section_id=cart-icon-bubble`);
      const cartIconBubbleText = await cartIconBubbleResponse.text();
      const cartIconBubbleHtml = new DOMParser().parseFromString(cartIconBubbleText, 'text/html');
      const newCartIconBubble = cartIconBubbleHtml.querySelector('.shopify-section');
      const currentCartIconBubble = document.getElementById('cart-icon-bubble');
      
      if (newCartIconBubble && currentCartIconBubble) {
        currentCartIconBubble.innerHTML = newCartIconBubble.innerHTML;
      }
      
    } catch (error) {
      console.error('Error updating cart drawer:', error);
    }
  }
  
  async updateInsuranceState() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      if (!response.ok) {
        console.error('Failed to fetch cart data for insurance state update');
        return;
      }
      
      const cartData = await response.json();
      
      const insuranceHandle = this.productHandle;
      let insuranceInCart = false;
      let insuranceItem = null;

      cartData.items.forEach((item) => {
        if (
          item.handle === insuranceHandle ||
          (item.properties && item.properties['_shipping_insurance'] === 'true')
        ) {
          insuranceInCart = true;
          insuranceItem = item;
        }
      });

      if (cartData.items.length === 1 && insuranceItem) {
        if (this.toggle) this.toggle.checked = false;
        await this.removeInsuranceFromCart(insuranceItem.variant_id, { system: true });
        return;
      }

      // Update toggle state
      if (this.toggle) {
        this.toggle.checked = insuranceInCart;
      }
      
      // Refresh component if state changed externally
      if (this.toggle && this.toggle.checked !== insuranceInCart) {
        await this.refreshComponent();
      }
      
    } catch (error) {
      console.error('Error updating insurance state:', error);
    }
  }
  
  async refreshComponent() {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const newInsuranceComponent = html.querySelector('.shipping-insurance');
      
      if (newInsuranceComponent) {
        // Store current parent for replacement
        const parent = this.parentNode;
        const newElement = document.createElement('shipping-insurance');
        newElement.innerHTML = newInsuranceComponent.innerHTML;
        
        // Copy data attributes
        Array.from(newInsuranceComponent.attributes).forEach(attr => {
          newElement.setAttribute(attr.name, attr.value);
        });
        
        parent.replaceChild(newElement, this);
      }
    } catch (error) {
      console.error('Error refreshing insurance component:', error);
    }
  }
  
  showLoading() {
    this.classList.add('is-loading');
    if (this.loadingElement) {
      this.loadingElement.removeAttribute('hidden');
    }
  }
  
  hideLoading() {
    this.classList.remove('is-loading');
    if (this.loadingElement) {
      this.loadingElement.setAttribute('hidden', '');
    }
  }
  
  showError(message) {
    if (this.errorElement && this.errorText) {
      this.errorText.textContent = message;
      this.errorElement.removeAttribute('hidden');
    }
  }
  
  hideError() {
    if (this.errorElement) {
      this.errorElement.setAttribute('hidden', '');
    }
  }
}

// An empty cart is a new cart: the next product added should get insurance.
if (typeof CartItemList !== 'undefined' && Array.isArray(CartItemList) && CartItemList.length === 0) {
  window.__shippingInsuranceCartWasEmpty = true;
  window.__shippingInsuranceOptOut = false;
}

// Define the custom element
customElements.define('shipping-insurance', ShippingInsurance);
// Helper function to setup image error handling
function setupImageErrorHandling(component) {
  const image = component.querySelector('.shipping-insurance__image');
  const svgWrapper = component.querySelector('.svg-wrapper');
  
  if (image && svgWrapper) {
    // Initially hide the SVG if image is present
    svgWrapper.style.display = 'none';
    
    image.addEventListener('error', function() {
      console.warn('Shipping insurance product image failed to load');
      image.style.display = 'none';
      svgWrapper.style.display = 'flex';
      svgWrapper.style.alignItems = 'center';
      svgWrapper.style.justifyContent = 'center';
    });
    
    image.addEventListener('load', function() {
      svgWrapper.style.display = 'none';
    });
    
    // Check if image is already loaded (cached)
    if (image.complete && image.naturalHeight !== 0) {
      svgWrapper.style.display = 'none';
    }
  }
}

// Handle cases where the cart drawer is dynamically loaded
document.addEventListener('DOMContentLoaded', function() {
  // Initialize any shipping insurance components that might be present
  const insuranceComponents = document.querySelectorAll('.shipping-insurance:not([is="shipping-insurance"])');
  insuranceComponents.forEach(component => {
    if (!component.matches('shipping-insurance')) {
      // Setup image error handling for non-custom elements
      setupImageErrorHandling(component);
      
      // Wrap in custom element if not already
      const wrapper = document.createElement('shipping-insurance');
      component.parentNode.insertBefore(wrapper, component);
      wrapper.appendChild(component);
    }
  });
});

// Re-initialize components after cart updates
if (typeof subscribe !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    const items = event.cartData && event.cartData.items;
    if (Array.isArray(items) && items.length === 0) {
      window.__shippingInsuranceCartWasEmpty = true;
      window.__shippingInsuranceOptOut = false;
      enqueueInsuranceTask(async () => {
        if (window.__shippingInsuranceCartWasEmpty !== true) return;
        try {
          await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              attributes: { [SHIPPING_INSURANCE_ATTR]: '' }
            })
          });
        } catch (error) {
          console.error('Error saving shipping insurance preference:', error);
        }
      });
    } else if (event.source === 'cart-items' && Array.isArray(items)) {
      const root = document.querySelector('.shipping-insurance');
      const handle = root?.dataset.productHandle;
      const stillThere = items.some(
        (item) => isInsuranceLine(item, handle) || String(item.variant_id) === String(root?.dataset.variantId)
      );

      if (!stillThere && window.__shippingInsuranceOptOut) {
        enqueueInsuranceTask(async () => {
          if (!window.__shippingInsuranceOptOut || window.__shippingInsuranceCartWasEmpty === true) return;
          try {
            await fetch(window.Shopify.routes.root + 'cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                attributes: { [SHIPPING_INSURANCE_ATTR]: 'false' }
              })
            });
          } catch (error) {
            console.error('Error saving shipping insurance preference:', error);
          }
        });
      }
    }

    // Re-initialize shipping insurance components after cart updates
    setTimeout(() => {
      const insuranceComponents = document.querySelectorAll('.shipping-insurance:not([is="shipping-insurance"])');
      insuranceComponents.forEach(component => {
        if (!component.closest('shipping-insurance')) {
          // Setup image error handling for newly loaded components
          setupImageErrorHandling(component);
          
          const wrapper = document.createElement('shipping-insurance');
          wrapper.innerHTML = component.outerHTML;
          component.replaceWith(wrapper);
        }
      });
    }, 100);
  });
}
